// CountryWelcome — premium "You are in Saudi Arabia — Calofay is the
// #1 calorie & diet app" hero shown once per install right after the
// user picks their language.
//
// Consumed by:
//   1. IOSOnboarding.jsx → new "country" step in the state machine
//   2. LandingV2.jsx → full-screen overlay on first visit
//
// Design contract
// ---------------
// - Brand orange gradient background (matches `orange-gradient` used
//   by every primary CTA in the app).
// - Large flag emoji floating on a glassmorphism card.
// - Gold "#1" badge with sparkle ring — signals prestige without
//   being tacky.
// - Country name in giant type (AR + EN).
// - One-line tagline: "أفضل تطبيق لحساب السعرات والدايت" / "The #1 app
//   for calorie tracking & diet".
// - Full-viewport, single CTA. User taps once → dismissed forever
//   (marked via markCountryWelcomeShown()).
//
// Bilingual — reads `calofay:lang` from localStorage so the screen
// respects the language the user just picked on the language step.

import { useEffect, useMemo, useState } from "react";
import { ArrowRight, ArrowLeft, Sparkles, Star } from "lucide-react";
import { motion } from "framer-motion";
import { getGulfCountryMeta, markCountryWelcomeShown } from "@/lib/gulfCountry";

const COPY = {
  ar: {
    // Small badge label above the flag disc — "The App" not "Calofay"
    // per owner request (Sep 30, 2026).
    brandLabel: "التطبيق",
    // Main headline (above the 5 stars). Owner (Sep 30, 2026) added
    // the word "رقم" before "#1" so it reads naturally in Arabic:
    // "التطبيق رقم #1 في السعودية".
    rankLine: (inCountry) => `التطبيق رقم #1 ${inCountry}`,
    tagline: "لحساب السعرات الحرارية والدايت والوصفات الصحية",
    // Subline below the 5 stars — visually supports the "highest-rated"
    // claim without over-promising subscriber counts.
    subline: (inCountry) => `التطبيق الأعلى تقييم ${inCountry}`,
    continue: "متابعة",
  },
  en: {
    brandLabel: "The App",
    rankLine: (inCountry) => `The #1 App ${inCountry}`,
    tagline: "for calorie tracking, diet & healthy recipes",
    subline: (inCountry) => `The highest-rated app ${inCountry}`,
    continue: "Continue",
  },
};

function readLang() {
  try {
    const l = localStorage.getItem("calofay:lang") || "ar";
    return l === "en" ? "en" : "ar";
  } catch { return "ar"; }
}

/**
 * @param {object}  props
 * @param {() => void} props.onContinue  — parent decides where to go next.
 * @param {string=} props.langOverride   — force AR/EN (used by LandingV2).
 */
export default function CountryWelcome({ onContinue, langOverride }) {
  const [lang, setLang] = useState(() => langOverride || readLang());
  const isRTL = lang === "ar";
  const t = COPY[lang];
  const country = useMemo(() => getGulfCountryMeta(), []);

  // Keep language in sync if the parent flips it while we're mounted
  // (rare — mostly a defensive hook for LandingV2's runtime toggle).
  useEffect(() => {
    if (langOverride && langOverride !== lang) setLang(langOverride);
  }, [langOverride, lang]);

  // Defensive: parent should never mount us for non-Gulf users, but
  // if they do we bail immediately instead of rendering broken UI.
  if (!country) return null;

  const inCountry = isRTL ? country.in_ar : country.in_en;
  const countryName = isRTL ? country.name_ar : country.name_en;

  const handleContinue = () => {
    markCountryWelcomeShown();
    onContinue?.();
  };

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className="always-light fixed inset-0 z-[95] overflow-hidden bg-[#FF6B1F]"
      data-testid="country-welcome-screen"
      data-country={country.code}
    >
      {/* Layered gradient + radial glows for a premium, non-flat look.
          Two overlapping radials + a diagonal linear give the surface
          depth without the "AI slop" purple-white gradient. */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 80% at 50% 0%, rgba(255,190,120,0.55) 0%, rgba(255,107,31,0) 60%), radial-gradient(80% 60% at 50% 100%, rgba(255,60,0,0.55) 0%, rgba(255,107,31,0) 70%), linear-gradient(160deg, #FF8C3A 0%, #FF6B1F 45%, #E5450F 100%)",
        }}
      />

      {/* Subtle noise texture (grain) softens the vector gradient so
          it doesn't band on OLED screens. Pure CSS — no asset. */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)' opacity='0.9'/%3E%3C/svg%3E\")",
        }}
      />

      {/* Sparkle motes — 12 twinkling stars drifting across the top
          half. Uses CSS animations so we don't spin a JS loop. */}
      <div aria-hidden className="absolute inset-x-0 top-0 h-1/2 pointer-events-none">
        {[...Array(14)].map((_, i) => (
          <span
            key={i}
            className="absolute text-white/70"
            style={{
              left: `${(i * 73) % 100}%`,
              top: `${(i * 41) % 90}%`,
              fontSize: `${8 + (i % 4) * 3}px`,
              animation: `cwSparkle ${2.5 + (i % 5) * 0.6}s ease-in-out ${i * 0.18}s infinite`,
            }}
          >
            ✦
          </span>
        ))}
      </div>

      <style>{`
        @keyframes cwSparkle {
          0%, 100% { opacity: 0; transform: scale(0.6); }
          50%      { opacity: 1; transform: scale(1); }
        }
        @keyframes cwFloat {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-6px); }
        }
        /* Staggered twinkle: each star pulses in scale + glow with a
           0.18s offset so the row looks like it's shimmering left→right.
           Uses drop-shadow instead of box-shadow so the halo hugs the
           star silhouette (not a rectangle around it). */
        @keyframes cwStarTwinkle {
          0%, 100% {
            transform: scale(1);
            filter: drop-shadow(0 0 0 rgba(255,211,90,0));
          }
          45% {
            transform: scale(1.22);
            filter: drop-shadow(0 0 10px rgba(255,225,120,0.95)) drop-shadow(0 0 20px rgba(255,180,50,0.55));
          }
        }
      `}</style>

      {/* Card + CTA column — sits above everything else. */}
      <div
        className="relative z-10 h-full w-full flex flex-col items-center justify-between px-6"
        style={{
          paddingTop: "calc(env(safe-area-inset-top) + 40px)",
          paddingBottom: "calc(env(safe-area-inset-bottom) + 32px)",
        }}
      >
        {/* Top brand label removed per owner (Sep 30, 2026) — the flag
            disc + rank headline own the hero on their own. Kept the
            spacer div so the flex layout still pushes the CTA to the
            bottom without a visible label competing with the flag. */}
        <div aria-hidden />

        {/* Main hero column */}
        <div className="flex-1 w-full flex flex-col items-center justify-center gap-5 -mt-4">
          {/* Flag disc with gold #1 badge tucked in the corner */}
          <motion.div
            initial={{ opacity: 0, scale: 0.6, rotate: -8 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ type: "spring", damping: 14, stiffness: 200, delay: 0.15 }}
            className="relative"
            style={{ animation: "cwFloat 4s ease-in-out infinite" }}
          >
            <div
              className="w-32 h-32 rounded-full flex items-center justify-center shadow-2xl"
              style={{
                background: "linear-gradient(140deg, #fff 0%, #FFF3E5 100%)",
                boxShadow: "0 20px 40px -12px rgba(0,0,0,0.35), 0 0 0 6px rgba(255,255,255,0.15), 0 0 0 12px rgba(255,255,255,0.08)",
              }}
              data-testid="country-welcome-flag"
            >
              <span className="text-[76px] leading-none select-none" aria-label={countryName}>
                {country.flag}
              </span>
            </div>

            {/* Gold #1 badge — top-end corner, mirrored for RTL */}
            <motion.div
              initial={{ opacity: 0, scale: 0.4, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ type: "spring", damping: 12, stiffness: 260, delay: 0.5 }}
              className={`absolute -bottom-2 ${isRTL ? "-left-2" : "-right-2"}`}
            >
              <div
                className="relative w-16 h-16 rounded-full flex items-center justify-center"
                style={{
                  background: "linear-gradient(145deg, #FFD35A 0%, #F5A623 50%, #C77800 100%)",
                  boxShadow: "0 8px 20px -6px rgba(199,120,0,0.7), inset 0 -3px 6px rgba(0,0,0,0.15), inset 0 3px 6px rgba(255,255,255,0.4)",
                }}
              >
                <span className="text-white font-black text-2xl drop-shadow-sm" style={{ textShadow: "0 2px 3px rgba(0,0,0,0.25)" }}>
                  #1
                </span>
                {/* Sparkle ring around the badge */}
                <Sparkles className="absolute -top-2 -right-1 w-4 h-4 text-yellow-200 fill-yellow-100 drop-shadow" />
              </div>
            </motion.div>
          </motion.div>

          {/* Rank line */}
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="text-white font-black text-center leading-tight text-[28px] sm:text-3xl px-3"
            data-testid="country-welcome-rank"
          >
            {t.rankLine(inCountry)}
          </motion.h1>

          {/* Tagline — slightly smaller, gold accent underline */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="text-center"
          >
            <p className="text-white/95 font-semibold text-lg sm:text-xl px-3 leading-relaxed">
              {t.tagline}
            </p>
            <div className="mx-auto mt-3 h-[3px] w-16 rounded-full bg-gradient-to-r from-transparent via-[#FFD35A] to-transparent" />
          </motion.div>

          {/* Stars + subline row — stars visually support the
              "highest-rated" claim; the subline names it in plain
              language, one line per language, no false subscriber
              counts. */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.7 }}
            className="flex flex-col items-center gap-2 mt-2"
          >
            <div className="flex items-center gap-1.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star
                  key={i}
                  className="w-8 h-8 text-[#FFD35A] fill-[#FFD35A]"
                  style={{
                    animation: `cwStarTwinkle 2.2s ease-in-out ${i * 0.18}s infinite`,
                  }}
                />
              ))}
            </div>
            <p
              className="text-white/90 text-sm font-semibold text-center leading-relaxed max-w-xs px-2"
              data-testid="country-welcome-subline"
            >
              {t.subline(inCountry)}
            </p>
          </motion.div>
        </div>

        {/* CTA */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.9 }}
          onClick={handleContinue}
          className="w-full max-w-sm bg-white text-[#E5450F] font-black py-4 rounded-full text-base flex items-center justify-center gap-2 active:scale-[0.98] transition-transform shadow-2xl hover:shadow-white/20"
          data-testid="country-welcome-continue"
        >
          <span>{t.continue}</span>
          {isRTL ? <ArrowLeft className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />}
        </motion.button>
      </div>
    </div>
  );
}
